import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import DashboardLayout from '../shared/layout/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import config from '../../config/config';
import { getMenuItemsByRole } from '../../utils/menuItems';
import { FaUser, FaEdit, FaCheck, FaUpload, FaTimes, FaLock } from "react-icons/fa";
import { FourSquare } from 'react-loading-indicators';
import '../../styles/Profile.css';

const ProfilePage = () => {
  const [profileData, setProfileData] = useState({
    id: '',
    username: '',
    fullname: '',
    kl: '',
    direktorat: '',
    email: '',
    hp: '',
    role: '',
    profileImage: null
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, feedback: '' });
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const passwordValidation = {
    minLength: 8,
    hasUpperCase: /[A-Z]/,
    hasLowerCase: /[a-z]/,
    hasNumber: /[0-9]/,
    hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/
  };

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/');
          return;
        }

        const decoded = jwtDecode(token);
        if (!decoded.twoFactorVerified) {
          navigate('/');
          return;
        }

        const response = await axios.get(`${config.API_URL}/profile`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        setProfileData(response.data);

        if (response.data.profileImage) {
          setImagePreview(response.data.profileImage);
        }
      } catch (err) {
        setMessage({
          type: 'error',
          text: 'Gagal mengambil data profil. Silakan coba lagi.'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prevData => ({
      ...prevData,
      [name]: value
    }));

    if (name === 'newPassword') {
      checkPasswordStrength(value);
    }
  };

  const checkPasswordStrength = (password) => {
    let score = 0;
    let feedback = [];

    if (password.length >= passwordValidation.minLength) score += 1;
    else feedback.push(`Kata sandi harus minimal ${passwordValidation.minLength} karakter`);

    if (passwordValidation.hasUpperCase.test(password)) score += 1;
    else feedback.push('Harus mengandung huruf besar');

    if (passwordValidation.hasLowerCase.test(password)) score += 1;
    else feedback.push('Harus mengandung huruf kecil');

    if (passwordValidation.hasNumber.test(password)) score += 1;
    else feedback.push('Harus mengandung angka');

    if (passwordValidation.hasSpecialChar.test(password)) score += 1;
    else feedback.push('Harus mengandung karakter khusus');

    setPasswordStrength({
      score,
      feedback: feedback.join(', ')
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setMessage({
          type: 'error',
          text: 'Ukuran file terlalu besar. Maksimal 2MB.'
        });
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        setMessage({
          type: 'error',
          text: 'Format file tidak didukung. Gunakan JPG, JPEG, atau PNG.'
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      setProfileData(prevData => ({
        ...prevData,
        profileImage: file
      }));
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const saveProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      const formData = new FormData();
      formData.append('id', profileData.id);
      formData.append('fullname', profileData.fullname);
      formData.append('kl', profileData.kl);
      formData.append('direktorat', profileData.direktorat);
      formData.append('email', profileData.email);
      formData.append('hp', profileData.hp);

      if (profileData.profileImage && typeof profileData.profileImage !== 'string') {
        formData.append('profileImage', profileData.profileImage);
      }

      const response = await axios.post(`${config.API_URL}/profile/update`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
      });

      setMessage({
        type: 'success',
        text: 'Profil berhasil diperbarui!'
      });

      setIsEditing(false);

      setProfileData(response.data);
      if (response.data.profileImage) {
        setImagePreview(response.data.profileImage);
      }

      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (err) {
      console.error("Error update profil:", err);
      setMessage({
        type: 'error',
        text: 'Gagal memperbarui profil. Silakan coba lagi.'
      });
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async () => {
    // Validasi di frontend
    if (!passwordData.currentPassword) {
      setMessage({
        type: 'error',
        text: 'Password saat ini harus diisi.'
      });
      return;
    }

    if (!passwordData.newPassword) {
      setMessage({
        type: 'error',
        text: 'Password baru harus diisi.'
      });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({
        type: 'error',
        text: 'Password baru dan konfirmasi tidak cocok.'
      });
      return;
    }

    if (passwordStrength.score < 5) {
      setMessage({
        type: 'error',
        text: `Password tidak memenuhi standar keamanan. ${passwordStrength.feedback}`
      });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      const response = await axios.post(`${config.API_URL}/profile/change-password`, passwordData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });

      setMessage({
        type: 'success',
        text: 'Password berhasil diubah!'
      });

      setIsChangingPassword(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordStrength({ score: 0, feedback: '' });

      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (err) {
      console.error("Error changing password:", err);

      // Handle error response
      if (err.response && err.response.data) {
        const errorData = err.response.data;

        // Cek apakah ini error password saat ini tidak valid
        if (errorData.error === 'INVALID_CURRENT_PASSWORD' ||
          (err.response.status === 400 && errorData.message.includes('tidak valid'))) {
          setMessage({
            type: 'error',
            text: 'Password saat ini tidak valid. Silakan periksa kembali.'
          });
        } else if (err.response.status === 400) {
          setMessage({
            type: 'error',
            text: errorData.message || 'Data yang dimasukkan tidak valid.'
          });
        } else if (err.response.status === 401) {
          setMessage({
            type: 'error',
            text: 'Sesi Anda telah berakhir. Silakan login kembali.'
          });
          setTimeout(() => {
            localStorage.removeItem('token');
            navigate('/');
          }, 2000);
        } else {
          setMessage({
            type: 'error',
            text: errorData.message || 'Gagal mengubah password. Silakan coba lagi.'
          });
        }
      } else {
        setMessage({
          type: 'error',
          text: 'Gagal mengubah password. Silakan coba lagi.'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const ProfileImageSection = () => {
    return (
      <div className="profile-image-container">
        {imagePreview ? (
          <img
            src={imagePreview}
            alt="Profil"
            className="profile-image"
            onError={(e) => {
              console.error("Gagal memuat gambar:", imagePreview);
              setImagePreview(null);
            }}
          />
        ) : (
          <div className="profile-image-placeholder">
            <FaUser size={60} />
          </div>
        )}
        {isEditing && (
          <button
            className="image-upload-button"
            onClick={triggerFileInput}
            title="Unggah foto profil"
          >
            <FaUpload />
          </button>
        )}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleImageChange}
          accept="image/jpeg,image/png,image/jpg"
          style={{ display: 'none' }}
        />
      </div>
    );
  };

  const getRoleName = () => {
    switch(profileData.role) {
      case 0: return 'Verifikator';
      case 1: return 'Pemerintah';
      case 2: return 'Supervisor';
      case 3: return 'Publikator';
      default: return 'User';
    }
  };

  return (
    <DashboardLayout menuItems={getMenuItemsByRole(profileData.role)} userRole={getRoleName()}>
      <div className="container mt-4 mb-5">
        <div className="row">
          <div className="col-md-4">
            <div className="card profile-card shadow-lg">
              <div className="card-body text-center">
                <ProfileImageSection />
                <h3 className="mt-3">{profileData.fullname || profileData.username}</h3>
                <p className="text-muted">
                  <strong>{getRoleName()}</strong>
                </p>

                {!isEditing && !isChangingPassword && (
                  <div className="d-grid gap-2">
                    <button
                      className="btn btn-primary"
                      onClick={() => setIsEditing(true)}
                    >
                      <FaEdit /> Edit Profil
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => setIsChangingPassword(true)}
                    >
                      <FaLock /> Ganti Kata Sandi
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-8">
            {message.text && (
              <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'}`}>
                {message.text}
              </div>
            )}

            {isEditing ? (
              <div className="card shadow-lg">
                <div className="card-header bg-primary text-white">
                  <h4 className="mb-0">Edit Profil</h4>
                </div>
                <div className="card-body">
                  <form>
                    <div className="mb-3">
                      <label className="form-label">Username</label>
                      <input
                        type="text"
                        className="form-control"
                        name="username"
                        value={profileData.username}
                        disabled
                      />
                      <small className="text-muted">Username tidak dapat diubah</small>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Nama Lengkap</label>
                      <input
                        type="text"
                        className="form-control"
                        name="fullname"
                        value={profileData.fullname || ''}
                        onChange={handleInputChange}
                        placeholder="Masukkan nama lengkap"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Kementerian/Lembaga</label>
                      <input
                        type="text"
                        className="form-control"
                        name="kl"
                        value={profileData.kl || '-'}
                        disabled
                      />
                      <small className="text-muted">Kementerian/Lembaga tidak dapat diubah</small>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Direktorat</label>
                      <input
                        type="text"
                        className="form-control"
                        name="direktorat"
                        value={profileData.direktorat || ''}
                        onChange={handleInputChange}
                        placeholder="Masukkan direktorat"
                      />
                    </div>

                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label className="form-label">Email</label>
                        <input
                          type="email"
                          className="form-control"
                          name="email"
                          value={profileData.email || ''}
                          onChange={handleInputChange}
                          placeholder="Masukkan email"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Nomor Telepon</label>
                        <input
                          type="text"
                          className="form-control"
                          name="hp"
                          value={profileData.hp || ''}
                          onChange={handleInputChange}
                          placeholder="Masukkan nomor telepon"
                        />
                      </div>
                    </div>

                    <div className="d-flex justify-content-end gap-2">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          setIsEditing(false);
                          setMessage({ type: '', text: '' });
                        }}
                      >
                        <FaTimes /> Batal
                      </button>
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={saveProfile}
                        disabled={loading}
                      >
                        <FaCheck /> Simpan
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            ) : isChangingPassword ? (
              <div className="card shadow-lg">
                <div className="card-header bg-primary text-white">
                  <h4 className="mb-0">Ganti Kata Sandi</h4>
                </div>
                <div className="card-body">
                  <form>
                    <div className="mb-3">
                      <label className="form-label">Kata Sandi Saat Ini</label>
                      <input
                        type="password"
                        className="form-control"
                        name="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        placeholder="Masukkan kata sandi saat ini"
                        autoComplete="current-password"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Kata Sandi Baru</label>
                      <input
                        type="password"
                        className="form-control"
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        placeholder="Masukkan kata sandi baru"
                        autoComplete="new-password"
                      />
                      {passwordData.newPassword && (
                        <div className="password-strength mt-2">
                          <div className="strength-meter">
                            {[...Array(5)].map((_, i) => (
                              <div
                                key={i}
                                className={`strength-segment ${i < passwordStrength.score ? 'active' : ''}`}
                                style={{
                                  backgroundColor: i < passwordStrength.score ?
                                    (passwordStrength.score < 3 ? '#ff4d4d' :
                                      passwordStrength.score < 5 ? '#ffa64d' : '#4CAF50') : ''
                                }}
                              ></div>
                            ))}
                          </div>
                          <small className={`d-block mt-1 ${passwordStrength.score === 5 ? 'text-success' : 'text-danger'}`}>
                            {passwordStrength.score === 5 ? 'Kata sandi kuat' : passwordStrength.feedback}
                          </small>
                          <small className="d-block mt-2 text-muted">
                            Kata sandi harus minimal 8 karakter, mengandung huruf besar, huruf kecil,
                            angka, dan karakter khusus (!@#$%^&*...)
                          </small>
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Konfirmasi Kata Sandi Baru</label>
                      <input
                        type="password"
                        className="form-control"
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        placeholder="Konfirmasi kata sandi baru"
                        autoComplete="new-password"
                      />
                      {passwordData.newPassword && passwordData.confirmPassword &&
                        passwordData.newPassword !== passwordData.confirmPassword && (
                          <small className="text-danger">Kata sandi tidak cocok</small>
                        )}
                    </div>

                    <div className="d-flex justify-content-end gap-2">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          setIsChangingPassword(false);
                          setPasswordData({
                            currentPassword: '',
                            newPassword: '',
                            confirmPassword: ''
                          });
                          setMessage({ type: '', text: '' });
                        }}
                      >
                        <FaTimes /> Batal
                      </button>
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={changePassword}
                        disabled={loading || passwordData.newPassword !== passwordData.confirmPassword || passwordStrength.score < 5}
                      >
                        <FaCheck /> Simpan
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            ) : (
              <div className="card shadow-lg">
                <div className="card-header bg-primary text-white">
                  <h4 className="mb-0">Informasi Profil</h4>
                </div>
                <div className="card-body">
                  <table className="table profile-table">
                    <tbody>
                      <tr>
                        <th>Username</th>
                        <td>{profileData.username}</td>
                      </tr>
                      <tr>
                        <th>Nama Lengkap</th>
                        <td>{profileData.fullname || '-'}</td>
                      </tr>
                      <tr>
                        <th>Kementerian / Lembaga</th>
                        <td>{profileData.kl || '-'}</td>
                      </tr>
                      <tr>
                        <th>Direktorat</th>
                        <td>{profileData.direktorat || '-'}</td>
                      </tr>
                      <tr>
                        <th>Email</th>
                        <td>{profileData.email || '-'}</td>
                      </tr>
                      <tr>
                        <th>Nomor Telepon</th>
                        <td>{profileData.hp || '-'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="loading-popup">
            <FourSquare color="#32cd32" size="medium" text="" textColor="" />
            <p>Memproses...</p>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default ProfilePage;